import React, { useState, useEffect } from "react";
import "../App.css";
import { apiRequest, API_ENDPOINTS, hasRole } from "../utils/api";

export default function AdminDashboard() {
    const [activeTab, setActiveTab] = useState("overview");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Data states
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [owners, setOwners] = useState([]);
    const [apartments, setApartments] = useState([]);
    const [safeTotal, setSafeTotal] = useState(0);

    // Form states
    const [newNotification, setNewNotification] = useState({
        title: "",
        message: "",
        apartment_tag: ""
    });
    const [assignOwner, setAssignOwner] = useState({
        owner_name: "",
        apartment_ids: []
    });
    const [selectedOwner, setSelectedOwner] = useState("");
    const [ownerAssignments, setOwnerAssignments] = useState([]);
    const [specialPayment, setSpecialPayment] = useState({
        owner_name: "",
        amount: "",
        description: "",
        paid_at: new Date().toISOString().slice(0, 10)
    });

    // New user form (admin only)
    const [newUser, setNewUser] = useState({
        username: "",
        password: "",
        confirmPassword: "",
        role: "staff"
    });
    const [creatingUser, setCreatingUser] = useState(false);

    // Salary tab: employees + salaries
    const [employees, setEmployees] = useState([]);
    const [salaries, setSalaries] = useState([]);
    const [salaryYear, setSalaryYear] = useState(new Date().getFullYear());
    const [newEmployee, setNewEmployee] = useState({ name: "", email: "" });
    const [addingEmployee, setAddingEmployee] = useState(false);
    const [salarySaving, setSalarySaving] = useState(null); // { employeeId, month } when saving
    const [editingAmount, setEditingAmount] = useState(null); // { employeeId, month, value } for inline edit

    const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    // Check if user is admin
    useEffect(() => {
        console.log("AdminDashboard: Checking user role...");
        console.log("Current role:", localStorage.getItem("role"));
        console.log("Has admin role:", hasRole("admin"));

        if (!hasRole("admin")) {
            console.log("User is not admin, redirecting to home");
            window.location.href = "/home";
            return;
        }

        console.log("User is admin, loading dashboard data");
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        setError("");

        try {
            console.log("Loading admin dashboard data...");
            console.log("API endpoints:", API_ENDPOINTS);

            // Test authentication first
            const token = localStorage.getItem("token");
            const role = localStorage.getItem("role");
            console.log("Auth token present:", !!token);
            console.log("User role:", role);

            if (!token) {
                setError("No authentication token found. Please log in again.");
                return;
            }

            if (role !== "admin") {
                setError("Access denied. Admin role required.");
                return;
            }

            // Test admin routes first
            console.log("Testing admin routes...");
            try {
                const testData = await apiRequest("/api/admin/test");
                console.log("Admin test result:", testData);
            } catch (testErr) {
                console.warn("Admin test failed:", testErr);
                if (testErr.message.includes("401") || testErr.message.includes("Missing token")) {
                    setError("Authentication failed. Please log in again.");
                    return;
                }
            }

            // Try to load stats
            try {
                console.log("Loading stats from:", API_ENDPOINTS.ADMIN_STATS);
                const statsData = await apiRequest(API_ENDPOINTS.ADMIN_STATS);
                console.log("Stats loaded:", statsData);
                setStats(statsData);
            } catch (err) {
                console.warn("Could not load stats:", err);
                // Create basic stats from apartments data
                const basicStats = {
                    users: { total_users: 0, blocked_users: 0, admin_count: 0, owner_count: 0, staff_count: 0 },
                    apartments: { total_apartments: 0, available_apartments: 0, rented_apartments: 0 },
                    invoices: { total_invoices: 0, total_revenue: 0 }
                };
                setStats(basicStats);
            }

            // Load basic data first
            const apartmentsData = await apiRequest("/api/apartments");
            console.log("Apartments loaded:", apartmentsData);
            setApartments(apartmentsData);

            // Try to load admin-specific data
            try {
                const usersData = await apiRequest(API_ENDPOINTS.ADMIN_USERS);
                console.log("Users loaded:", usersData);
                setUsers(usersData);
            } catch (err) {
                console.warn("Could not load users:", err);
                setUsers([]);
            }

            try {
                const notificationsData = await apiRequest(API_ENDPOINTS.ADMIN_NOTIFICATIONS);
                console.log("Notifications loaded:", notificationsData);
                setNotifications(notificationsData);
            } catch (err) {
                console.warn("Could not load notifications:", err);
                setNotifications([]);
            }

            try {
                const invoicesData = await apiRequest(API_ENDPOINTS.ADMIN_INVOICES);
                console.log("Invoices loaded:", invoicesData);
                setInvoices(invoicesData);
            } catch (err) {
                console.warn("Could not load invoices:", err);
                setInvoices([]);
            }

            try {
                const ownersData = await apiRequest(API_ENDPOINTS.ADMIN_OWNERS);
                console.log("Owners loaded:", ownersData);
                setOwners(ownersData);
            } catch (err) {
                console.warn("Could not load owners:", err);
                setOwners([]);
            }

            // Load safe (cash vault) summary
            try {
                const safeData = await apiRequest(API_ENDPOINTS.SAFE);
                setSafeTotal(safeData.total_balance || 0);
            } catch (safeErr) {
                console.warn("Could not load safe summary:", safeErr);
                setSafeTotal(0);
            }

            // Load assignments data separately
            try {
                const assignmentsData = await apiRequest("/api/admin/owners/assignments");
                console.log("Assignments loaded:", assignmentsData);
                setOwnerAssignments(assignmentsData);
            } catch (assignmentsError) {
                console.warn("Could not load owner assignments:", assignmentsError);
                // Create basic assignments from apartments data as fallback
                const basicAssignments = [...new Set(apartmentsData.map(a => a.owner_name).filter(Boolean))]
                    .map(ownerName => {
                        const ownerApts = apartmentsData.filter(a => a.owner_name === ownerName);
                        return {
                            owner_name: ownerName,
                            total_apartments: ownerApts.length,
                            rented_count: ownerApts.filter(a => a.status === 'Rented').length,
                            available_count: ownerApts.filter(a => a.status === 'Available').length,
                            yearly_income: ownerApts.reduce((sum, apt) => sum + (apt.price || 0) * 12, 0)
                        };
                    });
                setOwnerAssignments(basicAssignments);
            }

            console.log("All admin dashboard data loaded successfully");
        } catch (err) {
            console.error("Error loading dashboard data:", err);
            setError(`Failed to load dashboard data: ${err.message}`);

            // Try to load basic data as fallback
            try {
                console.log("Attempting fallback data loading...");
                const apartmentsData = await apiRequest("/api/apartments");
                setApartments(apartmentsData);
                console.log("Fallback apartments loaded:", apartmentsData);
            } catch (fallbackErr) {
                console.error("Fallback also failed:", fallbackErr);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBlockUser = async (userId, block) => {
        try {
            const endpoint = block ? `/api/admin/users/${userId}/block` : `/api/admin/users/${userId}/unblock`;
            await apiRequest(endpoint, { method: "PATCH" });
            setSuccess(`User ${block ? 'blocked' : 'unblocked'} successfully`);
            loadDashboardData();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleToggleSafeAccess = async (user) => {
        try {
            const allow = !user.can_use_safe;
            await apiRequest(`/api/admin/users/${user.id}/safe`, {
                method: "PATCH",
                body: JSON.stringify({ allow })
            });
            setSuccess(`Safe access ${allow ? "enabled" : "disabled"} for ${user.username}`);
            loadDashboardData();
        } catch (err) {
            setError(err.message || "Failed to update safe access");
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        if (!newUser.username || !newUser.password || !newUser.confirmPassword) {
            setError("Please fill in all fields.");
            return;
        }
        if (newUser.password !== newUser.confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        if (newUser.password.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }
        setCreatingUser(true);
        try {
            await apiRequest(API_ENDPOINTS.REGISTER, {
                method: "POST",
                body: JSON.stringify({
                    username: newUser.username,
                    password: newUser.password,
                    role: newUser.role
                })
            });
            setSuccess("Account created successfully.");
            setNewUser({ username: "", password: "", confirmPassword: "", role: "staff" });
            loadDashboardData();
        } catch (err) {
            setError(err.message || "Failed to create account.");
        } finally {
            setCreatingUser(false);
        }
    };

    const handleSendNotification = async (e) => {
        e.preventDefault();
        if (!newNotification.message) {
            setError("Message is required");
            return;
        }

        try {
            await apiRequest(API_ENDPOINTS.ADMIN_NOTIFICATIONS, {
                method: "POST",
                body: JSON.stringify(newNotification)
            });
            setSuccess("Notification sent successfully");
            setNewNotification({ title: "", message: "", apartment_tag: "" });
            loadDashboardData();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleAssignOwner = async (e) => {
        e.preventDefault();
        if (!assignOwner.owner_name || !assignOwner.apartment_ids?.length) {
            setError("Please select an owner and at least one apartment");
            return;
        }

        try {
            await apiRequest("/api/admin/assign-owner", {
                method: "POST",
                body: JSON.stringify({
                    owner_name: assignOwner.owner_name,
                    apartment_ids: assignOwner.apartment_ids
                })
            });
            setSuccess(`Owner '${assignOwner.owner_name}' assigned to ${assignOwner.apartment_ids.length} apartments`);
            setAssignOwner({ owner_name: "", apartment_ids: [] });
            setSelectedOwner("");
            loadDashboardData();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleOwnerSelect = async (ownerName) => {
        setSelectedOwner(ownerName);
        setAssignOwner({ ...assignOwner, owner_name: ownerName, apartment_ids: [] });

        if (ownerName) {
            try {
                const ownerApartments = await apiRequest(`/api/admin/owners/${encodeURIComponent(ownerName)}/apartments`);
                // Filter out apartments that already belong to this owner
                const availableApartments = apartments.filter(apt => apt.owner_name !== ownerName);
                setApartments(availableApartments);
            } catch (err) {
                console.error("Error fetching owner apartments:", err);
            }
        } else {
            // Reset to all apartments
            loadDashboardData();
        }
    };

    const handleDeleteNotification = async (id) => {
        if (!window.confirm("Are you sure you want to delete this notification?")) return;

        try {
            await apiRequest(`/api/admin/notifications/${id}`, { method: "DELETE" });
            setSuccess("Notification deleted successfully");
            loadDashboardData();
        } catch (err) {
            setError(err.message);
        }
    };

    // Salary tab: load employees + salaries for selected year
    const loadSalaryData = async () => {
        setError("");
        try {
            const [empData, salData] = await Promise.all([
                apiRequest(API_ENDPOINTS.ADMIN_EMPLOYEES),
                apiRequest(`${API_ENDPOINTS.ADMIN_SALARIES}?year=${salaryYear}`)
            ]);
            setEmployees(empData);
            setSalaries(salData);
        } catch (err) {
            setError(err.message || "Could not load salary data");
            setEmployees([]);
            setSalaries([]);
        }
    };

    useEffect(() => {
        if (activeTab === "salary") loadSalaryData();
    }, [activeTab, salaryYear]);

    const handleAddEmployee = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        if (!newEmployee.name?.trim()) {
            setError("Name is required");
            return;
        }
        setAddingEmployee(true);
        try {
            await apiRequest(API_ENDPOINTS.ADMIN_EMPLOYEES, {
                method: "POST",
                body: JSON.stringify({ name: newEmployee.name.trim(), email: newEmployee.email?.trim() || null })
            });
            setSuccess("Employee added");
            setNewEmployee({ name: "", email: "" });
            loadSalaryData();
        } catch (err) {
            setError(err.message);
        } finally {
            setAddingEmployee(false);
        }
    };

    const handleDeleteEmployee = async (id, name) => {
        if (!window.confirm(`Remove employee "${name}"? Salary records will also be deleted.`)) return;
        try {
            await apiRequest(`${API_ENDPOINTS.ADMIN_EMPLOYEES}/${id}`, { method: "DELETE" });
            setSuccess("Employee removed");
            loadSalaryData();
        } catch (err) {
            setError(err.message);
        }
    };

    const getSalaryFor = (employeeId, month) => {
        const r = salaries.find(s => s.employee_id === employeeId && s.month === month);
        return r ? { amount: r.amount, received: !!r.received } : { amount: 0, received: false };
    };

    const handleSalaryCellChange = async (employeeId, month, field, value) => {
        const current = getSalaryFor(employeeId, month);
        const amount = field === "amount" ? (parseFloat(value) || 0) : current.amount;
        const received = field === "received" ? !!value : current.received;
        setSalarySaving({ employeeId, month });
        setError("");
        try {
            await apiRequest(API_ENDPOINTS.ADMIN_SALARIES, {
                method: "PUT",
                body: JSON.stringify({
                    employee_id: employeeId,
                    year: salaryYear,
                    month,
                    amount,
                    received
                })
            });
            setSalaries(prev => {
                const rest = prev.filter(s => !(s.employee_id === employeeId && s.month === month));
                return [...rest, { employee_id: employeeId, year: salaryYear, month, amount, received: received ? 1 : 0 }];
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setSalarySaving(null);
        }
    };

    if (loading && !stats) {
        return (
            <div className="admin-dashboard">
                <div className="loading">Loading dashboard...</div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <h1>Admin Dashboard</h1>
                <p>Manage users, notifications, and system overview</p>
            </div>

            {error && <div className="alert error">{error}</div>}
            {success && <div className="alert success">{success}</div>}

            <div className="admin-tabs">
                <button
                    className={activeTab === "overview" ? "active" : ""}
                    onClick={() => setActiveTab("overview")}
                >
                    Overview
                </button>
                <button
                    className={activeTab === "users" ? "active" : ""}
                    onClick={() => setActiveTab("users")}
                >
                    Users
                </button>
                <button
                    className={activeTab === "notifications" ? "active" : ""}
                    onClick={() => setActiveTab("notifications")}
                >
                    Notifications
                </button>
                <button
                    className={activeTab === "invoices" ? "active" : ""}
                    onClick={() => setActiveTab("invoices")}
                >
                    Invoices
                </button>
                <button
                    className={activeTab === "owners" ? "active" : ""}
                    onClick={() => setActiveTab("owners")}
                >
                    Owner Management
                </button>
                <button
                    className={activeTab === "salary" ? "active" : ""}
                    onClick={() => setActiveTab("salary")}
                >
                    Salaris
                </button>
            </div>

            <div className="admin-content">
                {activeTab === "overview" && stats && (
                    <div className="overview-grid">
                        {/* Apartments card */}
                        <div className="stat-card">
                            <h3>Apartments</h3>
                            <div className="stat-number">{stats.apartments.total_apartments}</div>
                            <div className="stat-details">
                                <span>Available: {stats.apartments.available_apartments}</span>
                                <span>Rented: {stats.apartments.rented_apartments}</span>
                            </div>
                        </div>

                        {/* Invoices / revenue card */}
                        <div className="stat-card">
                            <h3>Invoices</h3>
                            <div className="stat-number">${stats.invoices.total_revenue || 0}</div>
                            <div className="stat-details">
                                <span>Total invoices: {stats.invoices.total_invoices}</span>
                            </div>
                        </div>

                        {/* Safe (cash vault) total card */}
                        <div className="stat-card">
                            <h3>Safe balance</h3>
                            <div className="stat-number">${safeTotal?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            <div className="stat-details">
                                <span>Total cash in safe (vault)</span>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "users" && (
                    <div className="users-section">
                        <h2>User Management</h2>

                        <div className="create-user-form glass-panel">
                            <h3>Create new account</h3>
                            <form onSubmit={handleCreateUser} className="form-inline-grid">
                                <div className="form-group">
                                    <label htmlFor="new-username">Username</label>
                                    <input
                                        id="new-username"
                                        type="text"
                                        value={newUser.username}
                                        onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                                        placeholder="Username"
                                        required
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="new-password">Password</label>
                                    <input
                                        id="new-password"
                                        type="password"
                                        value={newUser.password}
                                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                        placeholder="Min. 6 characters"
                                        required
                                        minLength={6}
                                        autoComplete="new-password"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="new-confirm">Confirm password</label>
                                    <input
                                        id="new-confirm"
                                        type="password"
                                        value={newUser.confirmPassword}
                                        onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                                        placeholder="Repeat password"
                                        required
                                        autoComplete="new-password"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="new-role">Role</label>
                                    <select
                                        id="new-role"
                                        value={newUser.role}
                                        onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                    >
                                        <option value="staff">Staff</option>
                                        <option value="owner">Owner</option>
                                    </select>
                                </div>
                                <div className="form-group form-group-btn">
                                    <button type="submit" className="btn-primary" disabled={creatingUser} aria-label="Create account">
                                        {creatingUser ? "Creating..." : "Create account"}
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="users-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Username</th>
                                        <th>Role</th>
                                        <th>Status</th>
                                        <th>Safe</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(user => (
                                        <tr key={user.id}>
                                            <td>{user.username}</td>
                                            <td>
                                                <span className={`role-badge ${user.role}`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td>
                                                {user.role === "staff" ? (
                                                    <button
                                                        type="button"
                                                        className={user.can_use_safe ? "btn-secondary" : "btn-primary"}
                                                        onClick={() => handleToggleSafeAccess(user)}
                                                    >
                                                        {user.can_use_safe ? "Allowed" : "No access"}
                                                    </button>
                                                ) : (
                                                    <span className="subtle">—</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={user.is_blocked ? "status-blocked" : "status-active"}>
                                                    {user.is_blocked ? "Blocked" : "Active"}
                                                </span>
                                            </td>
                                            <td className="cell-nowrap">{new Date(user.created_at).toLocaleDateString()}</td>
                                            <td>
                                                {user.role !== "admin" && (
                                                    <button
                                                        className={user.is_blocked ? "btn-unblock" : "btn-block"}
                                                        onClick={() => handleBlockUser(user.id, !user.is_blocked)}
                                                    >
                                                        {user.is_blocked ? "Unblock" : "Block"}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === "notifications" && (
                    <div className="notifications-section">
                        <h2>Send Notification</h2>
                        <form onSubmit={handleSendNotification} className="notification-form">
                            <div className="form-group">
                                <label>Title (optional)</label>
                                <input
                                    type="text"
                                    value={newNotification.title}
                                    onChange={(e) => setNewNotification({ ...newNotification, title: e.target.value })}
                                    placeholder="Notification title"
                                />
                            </div>
                            <div className="form-group">
                                <label>Message *</label>
                                <textarea
                                    value={newNotification.message}
                                    onChange={(e) => setNewNotification({ ...newNotification, message: e.target.value })}
                                    placeholder="Enter your message here..."
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Apartment Tag (optional)</label>
                                <input
                                    type="text"
                                    value={newNotification.apartment_tag}
                                    onChange={(e) => setNewNotification({ ...newNotification, apartment_tag: e.target.value })}
                                    placeholder="e.g., A101, A102"
                                />
                            </div>
                            <button type="submit" className="btn-primary">Send Notification</button>
                        </form>

                        <h2>Recent Notifications</h2>
                        <div className="notifications-list">
                            {notifications.map(notification => (
                                <div key={notification.id} className="notification-item">
                                    <div className="notification-content">
                                        <h4>{notification.title || "No title"}</h4>
                                        <p>{notification.message}</p>
                                        {notification.apartment_tag && (
                                            <span className="apartment-tag">#{notification.apartment_tag}</span>
                                        )}
                                        <small>{new Date(notification.created_at).toLocaleString()}</small>
                                    </div>
                                    <button
                                        className="btn-danger sm"
                                        onClick={() => handleDeleteNotification(notification.id)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === "invoices" && (
                    <div className="invoices-section">
                        <h2>All Invoices</h2>
                        <div className="invoices-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Apartment</th>
                                        <th>Employee</th>
                                        <th>Item</th>
                                        <th>Total</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map(invoice => (
                                        <tr key={invoice.id}>
                                            <td>{invoice.apartment_number}</td>
                                            <td>{invoice.employee_name}</td>
                                            <td>{invoice.item}</td>
                                            <td>{invoice.currency || "₺"}{invoice.total}</td>
                                            <td className="cell-nowrap">{new Date(invoice.created_at).toLocaleDateString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === "owners" && (
                    <div className="owners-section">
                        <h2>Assign Owner to Apartments</h2>
                        <form onSubmit={handleAssignOwner} className="assign-owner-form">
                            <div className="form-group">
                                <label>Select Owner</label>
                                <select
                                    value={selectedOwner}
                                    onChange={(e) => handleOwnerSelect(e.target.value)}
                                    required
                                >
                                    <option value="">Choose an owner...</option>
                                    {owners.map(owner => (
                                        <option key={owner.id} value={owner.username}>
                                            {owner.username}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedOwner && (
                                <div className="form-group">
                                    <label>Select Apartments to Assign (multiple allowed)</label>
                                    <select
                                        multiple
                                        value={assignOwner.apartment_ids || []}
                                        onChange={(e) => {
                                            const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                                            setAssignOwner({ ...assignOwner, apartment_ids: selected });
                                        }}
                                        size="6"
                                        required
                                    >
                                        {apartments
                                            // Show apartments that belong to this owner OR are unassigned.
                                            // Apartments of other owners are hidden.
                                            .filter(apt =>
                                                (apt.owner_name || "") === selectedOwner ||
                                                !apt.owner_name
                                            )
                                            .map(ap => (
                                                <option key={ap.id} value={ap.id}>
                                                    {ap.number} - {ap.type} - {ap.status}
                                                </option>
                                            ))}
                                    </select>
                                    <small className="hint">Hold CTRL (Windows) or ⌘ (Mac) to select multiple</small>
                                </div>
                            )}

                            <button type="submit" className="btn-primary" disabled={!selectedOwner}>
                                Assign Owner
                            </button>
                        </form>

                        <div className="payments-panel glass-panel">
                            <h3>Payments (deducted from owner total income)</h3>
                            <form
                                className="form-inline-grid"
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    try {
                                        await apiRequest(API_ENDPOINTS.ADMIN_SPECIAL_PAYMENTS, {
                                            method: "POST",
                                            body: JSON.stringify(specialPayment),
                                        });
                                        setSuccess("Payment saved. Owner total income is updated.");
                                        // Clear only amount/description, keep date
                                        setSpecialPayment((prev) => ({
                                            ...prev,
                                            amount: "",
                                            description: "",
                                        }));
                                        await loadDashboardData();
                                    } catch (err) {
                                        setError(err.message || "Failed to save payment");
                                    }
                                }}
                            >
                                <div className="form-group">
                                    <label>Owner</label>
                                    <select
                                        value={specialPayment.owner_name}
                                        onChange={(e) =>
                                            setSpecialPayment((prev) => ({ ...prev, owner_name: e.target.value }))
                                        }
                                        required
                                    >
                                        <option value="">Choose an owner...</option>
                                        {owners.map((owner) => (
                                            <option key={owner.id} value={owner.username}>
                                                {owner.username}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Amount</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={specialPayment.amount}
                                        onChange={(e) =>
                                            setSpecialPayment((prev) => ({ ...prev, amount: e.target.value }))
                                        }
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Date</label>
                                    <input
                                        type="date"
                                        value={specialPayment.paid_at}
                                        onChange={(e) =>
                                            setSpecialPayment((prev) => ({ ...prev, paid_at: e.target.value }))
                                        }
                                        required
                                    />
                                </div>
                                <div className="form-group full-width">
                                    <label>Description (reason)</label>
                                    <input
                                        type="text"
                                        value={specialPayment.description}
                                        onChange={(e) =>
                                            setSpecialPayment((prev) => ({ ...prev, description: e.target.value }))
                                        }
                                        placeholder="Example: charity donation, payment for someone..."
                                    />
                                </div>
                                <button type="submit" className="btn-secondary">
                                    Save payment
                                </button>
                            </form>
                        </div>

                        <h2>Owner Statistics & Assignments</h2>
                        <div className="owner-assignments">
                            {ownerAssignments.length > 0 ? (
                                ownerAssignments.map(assignment => (
                                    <div key={assignment.owner_name} className="assignment-item">
                                        <div className="owner-info">
                                            <strong>{assignment.owner_name}</strong>
                                            <div className="owner-stats">
                                                <span>Total: {assignment.total_apartments}</span>
                                                <span>Rented: {assignment.rented_count}</span>
                                                <span>Available: {assignment.available_count}</span>
                                                <span>Yearly Income: €{assignment.yearly_income || 0}</span>
                                            </div>
                                        </div>
                                        <div className="apartment-list">
                                            {apartments.filter(a => a.owner_name === assignment.owner_name).map(a => (
                                                <span key={a.id} className={`apartment-tag ${a.status.toLowerCase()}`}>
                                                    {a.number} ({a.status})
                                                </span>
                                            ))}
                                        </div>
                                        <div className="owner-actions">
                                            <button
                                                type="button"
                                                className="btn-danger sm"
                                                onClick={async () => {
                                                    const relatedApts = apartments.filter(a => a.owner_name === assignment.owner_name);
                                                    if (relatedApts.length === 0) return;
                                                    if (!window.confirm(`Remove owner '${assignment.owner_name}' from ${relatedApts.length} apartments?`)) {
                                                        return;
                                                    }
                                                    try {
                                                        await apiRequest("/api/admin/unassign-owner", {
                                                            method: "POST",
                                                            body: JSON.stringify({
                                                                owner_name: assignment.owner_name,
                                                                apartment_ids: relatedApts.map(a => a.id)
                                                            })
                                                        });
                                                        setSuccess(`Removed ${assignment.owner_name} from ${relatedApts.length} apartments`);
                                                        await loadDashboardData();
                                                    } catch (err) {
                                                        setError(err.message || "Failed to remove owner assignments");
                                                    }
                                                }}
                                            >
                                                Remove all assignments
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-data">
                                    <p>No owner assignments found. Assign apartments to owners to see statistics here.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "salary" && (
                    <div className="salary-section">
                        <div className="salary-header">
                            <h2>Salary overview</h2>
                            <p className="salary-subtitle">Manage employees and salary per month. Enter amount and check when received.</p>
                        </div>

                        <div className="salary-add-employee glass-panel">
                            <h3>Add new employee</h3>
                            <form onSubmit={handleAddEmployee} className="form-inline-grid salary-form">
                                <div className="form-group">
                                    <label htmlFor="emp-name">Name *</label>
                                    <input
                                        id="emp-name"
                                        type="text"
                                        value={newEmployee.name}
                                        onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                                        placeholder="Full name"
                                        required
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="emp-email">Email (optional)</label>
                                    <input
                                        id="emp-email"
                                        type="email"
                                        value={newEmployee.email}
                                        onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                                        placeholder="email@example.com"
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="form-group form-group-btn">
                                    <button type="submit" className="btn-primary" disabled={addingEmployee} aria-label="Add employee">
                                        {addingEmployee ? "Adding..." : "Add employee"}
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="salary-year-bar">
                            <label htmlFor="salary-year">Year:</label>
                            <select
                                id="salary-year"
                                value={salaryYear}
                                onChange={(e) => setSalaryYear(parseInt(e.target.value, 10))}
                                aria-label="Select year"
                            >
                                {[salaryYear - 2, salaryYear - 1, salaryYear, salaryYear + 1].map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>

                        <div className="salary-overview glass-panel">
                            <h3>Overview {salaryYear} — per employee per month</h3>
                            {employees.length === 0 ? (
                                <div className="no-data">
                                    <p>No employees yet. Add an employee above to register salary.</p>
                                </div>
                            ) : (
                                <div className="salary-table-wrap">
                                    <div className="salary-header-row" aria-hidden="true">
                                        <div className="salary-header-cell col-employee">Employee</div>
                                        {MONTH_NAMES.map((name, i) => (
                                            <div key={i} className="salary-header-cell col-month">{name}</div>
                                        ))}
                                    </div>
                                    <table className="salary-grid-table">
                                        <tbody>
                                            {employees.map(emp => (
                                                <tr key={emp.id}>
                                                    <td className="col-employee">
                                                        <div className="employee-cell">
                                                            <strong>{emp.name}</strong>
                                                            {emp.email && <span className="employee-email">{emp.email}</span>}
                                                            <button
                                                                type="button"
                                                                className="btn-danger sm btn-delete-employee"
                                                                onClick={() => handleDeleteEmployee(emp.id, emp.name)}
                                                                aria-label={`Remove ${emp.name}`}
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    </td>
                                                    {MONTH_NAMES.map((_, i) => {
                                                        const month = i + 1;
                                                        const { amount, received } = getSalaryFor(emp.id, month);
                                                        const isSaving = salarySaving?.employeeId === emp.id && salarySaving?.month === month;
                                                        const isEditing = editingAmount?.employeeId === emp.id && editingAmount?.month === month;
                                                        const displayAmount = isEditing ? editingAmount.value : (amount !== undefined && amount !== null && amount !== "" ? amount : "");
                                                        return (
                                                            <td key={month} className="col-month">
                                                                <div className="salary-cell">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        value={displayAmount}
                                                                        onChange={(e) => setEditingAmount({ employeeId: emp.id, month, value: e.target.value })}
                                                                        onBlur={(e) => {
                                                                            const v = e.target.value;
                                                                            setEditingAmount(null);
                                                                            if (parseFloat(v) !== amount || (v !== "" && (amount === 0 || amount === ""))) {
                                                                                handleSalaryCellChange(emp.id, month, "amount", v);
                                                                            }
                                                                        }}
                                                                        placeholder="0"
                                                                        className="salary-amount-input"
                                                                        aria-label={`Amount ${emp.name} ${MONTH_NAMES[i]}`}
                                                                    />
                                                                    <label className="salary-received-check">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={received}
                                                                            onChange={(e) => handleSalaryCellChange(emp.id, month, "received", e.target.checked)}
                                                                            aria-label={`Received ${emp.name} ${MONTH_NAMES[i]}`}
                                                                        />
                                                                        <span className="check-label">Received</span>
                                                                    </label>
                                                                    {isSaving && <span className="saving-dot" aria-hidden>●</span>}
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
